import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Link2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import PatientSearchSelect from '../../../components/common/PatientSearchSelect';
import type { UnifiedPatient } from '../../../types/unified-financial';
import {
  ASSESSMENT_REPORT_ATTACHMENT_MODE_CATALOG,
  ASSESSMENT_REPORT_AUDIENCE_CATALOG,
  ASSESSMENT_REPORT_DOMAIN_CATALOG,
  ASSESSMENT_REPORT_FINDING_STATUS_CATALOG,
  ASSESSMENT_REPORT_MODE_CATALOG,
  ASSESSMENT_REPORT_PURPOSE_CATALOG,
  CBDF_CHAPTER_CATALOG,
  CBDF_OFFICIAL_SOURCE_URI,
  ICF_CLASSIFICATION_LANGUAGE,
  ICF_CLASSIFICATION_SOURCE_URI,
  ICF_CLASSIFICATION_VERSION,
  ICF_DOMAIN_CATALOG,
  ICF_ENVIRONMENTAL_FACILITATOR_CATALOG,
  ICF_PROBLEM_QUALIFIER_CATALOG,
  formatIcfQualifiedCode,
  getAssessmentReportFindingStatusLabel,
  getIcfQualifierContextLabel,
  getIcfQualifierLabel,
  isCbdfFullCodeForChapter,
  isIcfCodeForDomain,
  isIcfFacilitatorQualifier,
  isIcfProblemQualifier,
  type AssessmentReportDomain,
  type AssessmentReportFindingStatus,
  type AssessmentReportMode,
  type CbdfChapterCode,
  type IcfDomain,
  type IcfProblemQualifier,
  type IcfQualifier,
  type IcfQualifierContext,
} from '../reports/assessmentReportCatalogs';
import {
  getAssessmentReportPeriod,
  validateAssessmentReportForm,
  type AssessmentReportCbdfEntryV2,
  type AssessmentReportEditorForm,
  type AssessmentReportHistoricalResultSnapshotV2,
  type AssessmentReportIcfEntryV2,
  type AssessmentReportSourceSnapshotV2,
} from '../reports/assessmentReportDomain';
import {
  reconcileAssessmentReportSuggestions,
} from '../reports/assessmentReportSuggestions';
import { getAssessmentReportPresentation } from '../reports/assessmentReportPresentation';
import type { AssessmentResultV2 } from '../types';
import { getAssessmentV2Label } from '../registry/assessmentRegistry';
import AssessmentLongitudinalPanel from './AssessmentLongitudinalPanel';
import AssessmentClinicalSourcesPanel from './AssessmentClinicalSourcesPanel';

interface AssessmentReportEditorModalProps {
  open: boolean;
  form: AssessmentReportEditorForm;
  patients: UnifiedPatient[];
  candidates: AssessmentResultV2[];
  candidatesLoading: boolean;
  saving: boolean;
  editing: boolean;
  canCreateTechnicalReport: boolean;
  onPatientChange: (patientId: string) => void;
  onToggleSource: (result: AssessmentResultV2) => void;
  onRemoveSource: (resultId: string) => void;
  onChange: (form: AssessmentReportEditorForm) => void;
  onClose: () => void;
  onSubmit: () => void;
  getResultMetric: (result: AssessmentResultV2) => string;
  getResultClassification: (result: AssessmentResultV2) => string;
}

type IcfDraft = {
  domain: IcfDomain;
  code: string;
  qualifierContext: IcfQualifierContext | '';
  qualifier: string;
  capacityQualifier: string;
  professionalDescription: string;
  note: string;
  evidenceResultIds: string[];
};

const TECHNICAL_REPORT_TITLE = 'Relatório técnico com diagnóstico fisioterapêutico';
const LEGACY_TECHNICAL_REPORT_TITLE = 'Relatório técnico fisioterapêutico';

type ReportDocumentModel = 'summary' | 'initial' | 'final' | 'longitudinal' | 'findings';

const documentModelTitles: Record<ReportDocumentModel, string> = {
  summary: 'Resumo das avaliações',
  initial: 'Avaliação fisioterapêutica inicial',
  final: 'Avaliação fisioterapêutica final',
  longitudinal: 'Relatório longitudinal de avaliação fisioterapêutica',
  findings: 'Relatório de achados físico-funcionais e diagnóstico fisioterapêutico',
};

const documentModels = [
  {
    value: 'initial',
    label: 'Avaliação inicial ou final',
    description: 'História, exame, testes selecionados, síntese, metas e plano em um documento.',
  },
  {
    value: 'longitudinal',
    label: 'Longitudinal / pré–pós',
    description: 'Resultados de duas ou mais datas, medidas pontuais e análise da evolução.',
  },
  {
    value: 'findings',
    label: 'Achados físico-funcionais com CBDF',
    description: 'Evidências, repercussões funcionais e diagnóstico confirmado pelo fisioterapeuta.',
  },
] as const;

const emptyIcfDraft = (): IcfDraft => ({
  domain: 'body_functions',
  code: '',
  qualifierContext: '',
  qualifier: '',
  capacityQualifier: '',
  professionalDescription: '',
  note: '',
  evidenceResultIds: [],
});

const splitLines = (value: string): string[] =>
  value ? value.split(/\r?\n/) : [];

const joinLines = (values: readonly string[]): string => values.join('\n');

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Data não informada' : date.toLocaleDateString('pt-BR');
};

const revisionsMatch = (
  expectedUpdatedAt: string | null,
  expectedCreatedAt: string | null,
  currentUpdatedAt?: string | null,
  currentCreatedAt?: string | null,
): boolean => {
  const expected = expectedUpdatedAt?.trim() || expectedCreatedAt?.trim() || '';
  const current = currentUpdatedAt?.trim() || currentCreatedAt?.trim() || '';
  if (!expected || !current) return false;

  const expectedTime = Date.parse(expected);
  const currentTime = Date.parse(current);
  return Number.isFinite(expectedTime) && Number.isFinite(currentTime)
    ? expectedTime === currentTime
    : expected === current;
};

const findingTone: Record<AssessmentReportFindingStatus, string> = {
  within_expected: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  borderline: 'border-amber-200 bg-amber-50 text-amber-800',
  altered: 'border-rose-200 bg-rose-50 text-rose-800',
  not_classified: 'border-slate-200 bg-slate-50 text-slate-700',
  not_interpretable: 'border-gray-300 bg-gray-100 text-gray-700',
};

const sourceProfessionalLabel = (source: AssessmentReportSourceSnapshotV2): string => {
  const professional = source.professional;
  return [professional.name, professional.registration].filter(Boolean).join(' • ') || 'Profissional não identificado';
};

const sourceEvidenceLabel = (source: AssessmentReportSourceSnapshotV2): string =>
  `${source.snapshot.testLabel} · ${formatDate(source.snapshot.assessmentDate)}`;

const withMode = (
  current: AssessmentReportEditorForm,
  mode: AssessmentReportMode,
): AssessmentReportEditorForm => {
  if (mode === current.mode) return current;

  if (mode === 'assessment_summary') {
    return {
      ...current,
      mode,
      documentModel: current.documentModel === 'findings' ? 'summary' : current.documentModel,
      title:
        current.title === TECHNICAL_REPORT_TITLE
        || current.title === LEGACY_TECHNICAL_REPORT_TITLE
          ? 'Resumo das avaliações'
          : current.title,
      content: {
        mode,
        origin: 'professional_authored',
        professionalOverview:
          current.mode === 'physiotherapy_technical_report'
            ? current.content.integratedAnalysis
            : '',
        followUpGuidance:
          current.mode === 'physiotherapy_technical_report'
            ? current.content.recommendations.join('\n') || null
            : null,
        observations: current.mode === 'physiotherapy_technical_report' && current.content.limitations
          ? [current.content.limitations]
          : [],
      },
      cbdf: {
        ...current.cbdf,
        entries: [],
        professionalNote: null,
      },
      icf: {
        ...current.icf,
        domains: {
          bodyFunctions: [],
          bodyStructures: [],
          activitiesAndParticipation: [],
          environmentalFactors: [],
        },
        professionalSynthesis: null,
      },
    };
  }

  return {
    ...current,
    mode,
    title:
      current.title === 'Resumo das avaliações'
        ? TECHNICAL_REPORT_TITLE
        : current.title,
    content: {
      mode,
      origin: 'professional_authored',
      clinicalContext: '',
      integratedAnalysis:
        current.mode === 'assessment_summary'
          ? current.content.professionalOverview
          : '',
      physiotherapyConclusion: '',
      recommendations:
        current.mode === 'assessment_summary' && current.content.followUpGuidance
          ? splitLines(current.content.followUpGuidance)
          : [],
      limitations: current.mode === 'assessment_summary'
        ? current.content.observations.join('\n') || null
        : null,
    },
  };
};

const getIcfEntries = (
  form: AssessmentReportEditorForm,
): AssessmentReportIcfEntryV2[] => [
  ...form.icf.domains.bodyFunctions,
  ...form.icf.domains.bodyStructures,
  ...form.icf.domains.activitiesAndParticipation,
  ...form.icf.domains.environmentalFactors,
];

const hasTechnicalOnlyContent = (form: AssessmentReportEditorForm): boolean =>
  form.mode === 'physiotherapy_technical_report'
  && Boolean(
    form.content.clinicalContext.trim()
    || form.content.integratedAnalysis.trim()
    || form.content.physiotherapyConclusion.trim()
    || form.content.recommendations.length
    || form.content.limitations?.trim()
    || form.cbdf.entries.length
    || form.cbdf.professionalNote?.trim()
    || getIcfEntries(form).length
    || form.icf.professionalSynthesis?.trim(),
  );

const replaceIcfEntries = (
  form: AssessmentReportEditorForm,
  entries: AssessmentReportIcfEntryV2[],
): AssessmentReportEditorForm => ({
  ...form,
  icf: {
    ...form.icf,
    domains: {
      bodyFunctions: entries.filter(
        (entry): entry is AssessmentReportIcfEntryV2<'body_functions'> =>
          entry.domain === 'body_functions',
      ),
      bodyStructures: entries.filter(
        (entry): entry is AssessmentReportIcfEntryV2<'body_structures'> =>
          entry.domain === 'body_structures',
      ),
      activitiesAndParticipation: entries.filter(
        (entry): entry is AssessmentReportIcfEntryV2<'activities_and_participation'> =>
          entry.domain === 'activities_and_participation',
      ),
      environmentalFactors: entries.filter(
        (entry): entry is AssessmentReportIcfEntryV2<'environmental_factors'> =>
          entry.domain === 'environmental_factors',
      ),
    },
  },
});

const AssessmentReportEditorModal = ({
  open,
  form: incomingForm,
  patients,
  candidates,
  candidatesLoading,
  saving,
  editing,
  canCreateTechnicalReport,
  onPatientChange,
  onToggleSource,
  onRemoveSource,
  onChange,
  onClose,
  onSubmit,
  getResultMetric,
  getResultClassification,
}: AssessmentReportEditorModalProps) => {
  const presentationSynced = useRef(false);
  const presentedForm = useMemo(() => getAssessmentReportPresentation(incomingForm), [incomingForm]);
  const form = presentationSynced.current ? incomingForm : presentedForm;
  const dialogRef = useRef<HTMLDivElement>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceTestFilter, setSourceTestFilter] = useState('all');
  const [sourceProfessionalFilter, setSourceProfessionalFilter] = useState('all');
  const [sourceDateFrom, setSourceDateFrom] = useState('');
  const [sourceDateTo, setSourceDateTo] = useState('');
  const [cbdfChapter, setCbdfChapter] = useState<CbdfChapterCode | ''>('');
  const [cbdfCode, setCbdfCode] = useState('');
  const [cbdfStatement, setCbdfStatement] = useState('');
  const [cbdfRationale, setCbdfRationale] = useState('');
  const [cbdfEvidenceResultIds, setCbdfEvidenceResultIds] = useState<string[]>([]);
  const [icfDraft, setIcfDraft] = useState<IcfDraft>(emptyIcfDraft());

  useEffect(() => {
    if (!open) {
      presentationSynced.current = false;
      return;
    }
    if (presentationSynced.current) return;
    presentationSynced.current = true;
    if (form !== incomingForm) onChange(form);
  }, [form, incomingForm, onChange, open]);

  useEffect(() => {
    if (!open) return;
    setConfirmed(false);
    setSourceSearch('');
    setSourceTestFilter('all');
    setSourceProfessionalFilter('all');
    setSourceDateFrom('');
    setSourceDateTo('');
    setCbdfChapter('');
    setCbdfCode('');
    setCbdfStatement('');
    setCbdfRationale('');
    setCbdfEvidenceResultIds([]);
    setIcfDraft(emptyIcfDraft());
  }, [open, form.patient.patientId]);

  useEffect(() => {
    if (open) setConfirmed(false);
  }, [form, open]);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || saving) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open, saving]);

  useEffect(() => {
    const sourceIds = new Set(form.sources.map((source) => source.resultId));
    setCbdfEvidenceResultIds((current) => current.filter((id) => sourceIds.has(id)));
    setIcfDraft((current) => ({
      ...current,
      evidenceResultIds: current.evidenceResultIds.filter((id) => sourceIds.has(id)),
    }));
  }, [form.sources]);

  const emit = (
    next: AssessmentReportEditorForm,
    options: { fillEmpty?: boolean } = {},
  ) => {
    setConfirmed(false);
    onChange(
      reconcileAssessmentReportSuggestions(form, next, {
        fillEmpty: options.fillEmpty ?? false,
      }),
    );
  };

  const documentModel: ReportDocumentModel = form.documentModel
    ?? (form.mode === 'physiotherapy_technical_report' ? 'findings' : 'summary');

  const changeDocumentModel = (nextModel: ReportDocumentModel) => {
    if (nextModel === 'findings' && (!canCreateTechnicalReport || (editing && form.mode !== 'physiotherapy_technical_report'))) return;
    const next = nextModel === 'findings'
      ? withMode(form, 'physiotherapy_technical_report')
      : form;
    const automaticTitles = new Set([
      ...Object.values(documentModelTitles),
      TECHNICAL_REPORT_TITLE,
      LEGACY_TECHNICAL_REPORT_TITLE,
    ]);
    emit({
      ...next,
      documentModel: nextModel,
      title: automaticTitles.has(form.title) ? documentModelTitles[nextModel] : form.title,
    }, { fillEmpty: true });
  };

  const validation = useMemo(() => validateAssessmentReportForm(form), [form]);
  const period = useMemo(() => getAssessmentReportPeriod(form.sources), [form.sources]);
  const priorityEvidenceSources = useMemo(
    () =>
      form.sources.filter(
        (source) =>
          source.finding.status === 'altered'
          || source.finding.status === 'borderline',
      ),
    [form.sources],
  );
  const selectedSourceIds = useMemo(
    () => new Set(form.sources.map((source) => source.resultId)),
    [form.sources],
  );
  const candidateById = useMemo(
    () => new Map(candidates.map((candidate) => [candidate.resultId, candidate])),
    [candidates],
  );
  const testOptions = useMemo(
    () =>
      [...new Set(candidates.map((candidate) => candidate.testKey))]
        .sort((left, right) =>
          getAssessmentV2Label(left).localeCompare(getAssessmentV2Label(right), 'pt-BR'),
        ),
    [candidates],
  );
  const professionalOptions = useMemo(
    () =>
      [...new Set(
        candidates
          .map((candidate) => candidate.professionalNameSnapshot?.trim())
          .filter((value): value is string => Boolean(value)),
      )].sort((left, right) => left.localeCompare(right, 'pt-BR')),
    [candidates],
  );
  const filteredCandidates = useMemo(() => {
    const search = sourceSearch.trim().toLocaleLowerCase('pt-BR');
    const from = sourceDateFrom ? new Date(`${sourceDateFrom}T00:00:00`).getTime() : null;
    const to = sourceDateTo ? new Date(`${sourceDateTo}T23:59:59.999`).getTime() : null;

    return candidates.filter((candidate) => {
      const date = new Date(candidate.assessmentDate).getTime();
      if (sourceTestFilter !== 'all' && candidate.testKey !== sourceTestFilter) return false;
      if (
        sourceProfessionalFilter !== 'all'
        && candidate.professionalNameSnapshot !== sourceProfessionalFilter
      ) {
        return false;
      }
      if (from != null && date < from) return false;
      if (to != null && date > to) return false;
      if (!search) return true;
      return [
        getAssessmentV2Label(candidate.testKey),
        getResultMetric(candidate),
        getResultClassification(candidate),
        candidate.professionalNameSnapshot,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('pt-BR')
        .includes(search);
    });
  }, [
    candidates,
    getResultClassification,
    getResultMetric,
    sourceDateFrom,
    sourceDateTo,
    sourceProfessionalFilter,
    sourceSearch,
    sourceTestFilter,
  ]);

  const updateSource = (
    resultId: string,
    updater: (source: AssessmentReportSourceSnapshotV2) => AssessmentReportSourceSnapshotV2,
  ) => {
    emit({
      ...form,
      sources: form.sources.map((source) =>
        source.resultId === resultId ? updater(source) : source,
      ),
    });
  };

  const toggleAttachment = (resultId: string) => {
    const current = form.exportOptions.attachments.resultIds;
    const resultIds = current.includes(resultId)
      ? current.filter((id) => id !== resultId)
      : [...current, resultId];
    emit({
      ...form,
      exportOptions: {
        ...form.exportOptions,
        attachments: { mode: 'selected_assessments', resultIds },
      },
    });
  };

  const buildHistoricalResultSnapshot = (
    result: AssessmentResultV2,
  ): AssessmentReportHistoricalResultSnapshotV2 => {
    const classification = getResultClassification(result).trim();
    return {
      resultId: result.resultId,
      sessionId: result.sessionId,
      testKey: result.testKey,
      testVersion: result.testVersion,
      assessmentDate: result.assessmentDate,
      sourceStatus: result.status,
      sourceCreatedAt: result.createdAt ?? null,
      sourceUpdatedAt: result.updatedAt ?? result.createdAt ?? null,
      capturedAt: new Date().toISOString(),
      primaryResult: getResultMetric(result).trim(),
      classificationLabel:
        classification && classification !== '—' ? classification : null,
      professional: {
        userId: result.professionalUserId ?? null,
        name: result.professionalNameSnapshot ?? null,
        registration: result.professionalRegistrationSnapshot ?? null,
        role: result.professionalRoleSnapshot ?? null,
      },
    };
  };

  const removeHistoricalResult = (
    sourceResultId: string,
    historicalResultId: string,
  ) => {
    const currentSelection = form.exportOptions.history.selections.find(
      (selection) => selection.sourceResultId === sourceResultId,
    );
    if (!currentSelection) return;

    const historicalResultIds = currentSelection.historicalResultIds.filter(
      (id) => id !== historicalResultId,
    );
    const historicalResultSnapshots = currentSelection.historicalResultSnapshots.filter(
      (snapshot) => snapshot.resultId !== historicalResultId,
    );
    const remaining = form.exportOptions.history.selections.filter(
      (selection) => selection.sourceResultId !== sourceResultId,
    );
    const selections = historicalResultIds.length
      ? [
          ...remaining,
          {
            sourceResultId,
            historicalResultIds,
            historicalResultSnapshots,
          },
        ]
      : remaining;
    emit({
      ...form,
      exportOptions: {
        ...form.exportOptions,
        history: { mode: 'selected', selections },
      },
    });
  };

  const selectHistoricalResult = (
    sourceResultId: string,
    result: AssessmentResultV2,
  ) => {
    const currentSelection = form.exportOptions.history.selections.find(
      (selection) => selection.sourceResultId === sourceResultId,
    );
    const historicalResultIds = currentSelection?.historicalResultIds.includes(result.resultId)
      ? currentSelection.historicalResultIds
      : [...(currentSelection?.historicalResultIds ?? []), result.resultId];
    const snapshot = buildHistoricalResultSnapshot(result);
    const historicalResultSnapshots = [
      ...(currentSelection?.historicalResultSnapshots ?? []).filter(
        (item) => item.resultId !== result.resultId,
      ),
      snapshot,
    ];
    const remaining = form.exportOptions.history.selections.filter(
      (selection) => selection.sourceResultId !== sourceResultId,
    );
    emit({
      ...form,
      exportOptions: {
        ...form.exportOptions,
        history: {
          mode: 'selected',
          selections: [
            ...remaining,
            {
              sourceResultId,
              historicalResultIds,
              historicalResultSnapshots,
            },
          ],
        },
      },
    });
  };

  const toggleHistoricalResult = (
    sourceResultId: string,
    result: AssessmentResultV2,
  ) => {
    const currentSelection = form.exportOptions.history.selections.find(
      (selection) => selection.sourceResultId === sourceResultId,
    );
    if (currentSelection?.historicalResultIds.includes(result.resultId)) {
      removeHistoricalResult(sourceResultId, result.resultId);
      return;
    }
    selectHistoricalResult(sourceResultId, result);
  };

  const toggleDraftEvidence = (
    resultId: string,
    values: string[],
    setter: (values: string[]) => void,
  ) => {
    setter(
      values.includes(resultId)
        ? values.filter((id) => id !== resultId)
        : [...values, resultId],
    );
  };

  const addCbdfEntry = () => {
    if (!cbdfChapter || !cbdfStatement.trim() || !cbdfRationale.trim()) return;
    if (!isCbdfFullCodeForChapter(cbdfCode, cbdfChapter)) return;
    if (cbdfEvidenceResultIds.length === 0) return;

    const now = new Date().toISOString();
    const entry: AssessmentReportCbdfEntryV2 = {
      id: crypto.randomUUID(),
      chapterCode: cbdfChapter,
      manualCode: cbdfCode.trim(),
      professionalStatement: cbdfStatement.trim(),
      rationale: cbdfRationale.trim(),
      evidenceResultIds: cbdfEvidenceResultIds,
      origin: 'manual_professional_entry',
      recordedBy: form.authorship.professional,
      recordedAt: now,
    };
    emit({
      ...form,
      cbdf: { ...form.cbdf, entries: [...form.cbdf.entries, entry] },
    });
    setCbdfChapter('');
    setCbdfCode('');
    setCbdfStatement('');
    setCbdfRationale('');
    setCbdfEvidenceResultIds([]);
  };

  const addIcfEntry = () => {
    const normalizedCode = icfDraft.code.trim().toLowerCase();
    if (
      !isIcfCodeForDomain(normalizedCode, icfDraft.domain)
      || !icfDraft.professionalDescription.trim()
      || !icfDraft.qualifierContext
      || !icfDraft.qualifier
      || icfDraft.evidenceResultIds.length === 0
    ) {
      return;
    }

    const numericQualifier = Number(icfDraft.qualifier);
    const qualifier: IcfQualifier =
      icfDraft.qualifier.startsWith('+')
        ? icfDraft.qualifier as IcfQualifier
        : numericQualifier as IcfQualifier;
    if (
      !isIcfProblemQualifier(qualifier)
      && !(icfDraft.domain === 'environmental_factors' && isIcfFacilitatorQualifier(qualifier))
    ) {
      return;
    }
    const capacityQualifier =
      icfDraft.domain === 'activities_and_participation'
      && icfDraft.capacityQualifier !== ''
        ? Number(icfDraft.capacityQualifier)
        : null;
    if (
      icfDraft.domain === 'activities_and_participation'
      && (
        icfDraft.qualifierContext !== 'performance'
        || (
          capacityQualifier !== null
          && !isIcfProblemQualifier(capacityQualifier)
        )
      )
    ) {
      return;
    }
    if (
      icfDraft.domain === 'environmental_factors'
      && (
        (icfDraft.qualifierContext === 'facilitator' && !isIcfFacilitatorQualifier(qualifier))
        || (icfDraft.qualifierContext === 'barrier' && !isIcfProblemQualifier(qualifier))
      )
    ) {
      return;
    }

    const entry: AssessmentReportIcfEntryV2 = {
      id: crypto.randomUUID(),
      domain: icfDraft.domain,
      code: normalizedCode as AssessmentReportIcfEntryV2['code'],
      qualifierContext: icfDraft.qualifierContext,
      qualifier: qualifier as AssessmentReportIcfEntryV2['qualifier'],
      capacityQualifier: capacityQualifier as IcfProblemQualifier | null,
      professionalDescription: icfDraft.professionalDescription.trim(),
      note: icfDraft.note.trim() || null,
      evidenceResultIds: icfDraft.evidenceResultIds,
      origin: 'manual_professional_entry',
      recordedBy: form.authorship.professional,
      recordedAt: new Date().toISOString(),
    };
    emit(replaceIcfEntries(form, [...getIcfEntries(form), entry]));
    setIcfDraft(emptyIcfDraft());
  };

  if (!open) return null;

  const technicalUnavailable =
    form.mode === 'physiotherapy_technical_report' && !canCreateTechnicalReport;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assessment-report-editor-title"
        aria-describedby="assessment-report-editor-description"
        tabIndex={-1}
        className="flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] bg-slate-50 shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 md:px-7">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs font-black uppercase tracking-[0.22em] text-primary-600">
              <FileText className="h-4 w-4" />
              Documento de avaliação
            </div>
            <h2 id="assessment-report-editor-title" className="text-xl font-bold text-slate-950">
              {editing ? 'Editar documento de avaliação' : 'Gerar documento de avaliação'}
            </h2>
            <p id="assessment-report-editor-description" className="mt-1 max-w-3xl text-sm text-slate-600">
              Selecione os resultados e registre sua análise, o plano e as observações que deseja incluir.
            </p>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 md:px-7">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-950">1. Identificação e finalidade</h3>
                <p className="text-sm text-slate-500">Defina para quem o documento será entregue e por quê.</p>
              </div>
              {period && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  Período avaliado: {period.label}
                </span>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Paciente</label>
                {editing ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                    {form.patient.name}
                    {form.patient.medicalRecord ? ` • ${form.patient.medicalRecord}` : ''}
                  </div>
                ) : (
                  <PatientSearchSelect
                    patients={patients}
                    selectedPatientId={form.patient.patientId}
                    onSelect={onPatientChange}
                    showActiveFilter={false}
                    allowInactiveSelection={false}
                    allowManualOther={false}
                    placeholder="Buscar paciente..."
                  />
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Título</label>
                <input
                  aria-label="Título do relatório"
                  value={form.title}
                  onChange={(event) => emit({ ...form, title: event.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-bold text-slate-900">Modelo do documento</legend>
              <p className="mt-1 text-sm text-slate-500">
                Escolha a organização do documento. O diagnóstico CBDF pode compor qualquer modelo no modo técnico.
              </p>
              <div className="mt-3 grid gap-3 lg:grid-cols-3">
                {documentModels.map((item) => {
                  const selected = item.value === 'initial'
                    ? documentModel === 'initial' || documentModel === 'final'
                    : documentModel === item.value;
                  const disabled = item.value === 'findings'
                    && (!canCreateTechnicalReport || (editing && form.mode !== 'physiotherapy_technical_report'));
                  return (
                    <label key={item.value} className={`flex gap-3 rounded-2xl border p-4 ${selected ? 'border-primary-300 bg-primary-50' : 'border-slate-200 bg-white'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="assessment-report-document-model"
                        value={item.value}
                        checked={selected}
                        disabled={disabled}
                        onChange={() => changeDocumentModel(item.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-bold text-slate-900">{item.label}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-slate-600">{item.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {(documentModel === 'initial' || documentModel === 'final') && (
                <label className="mt-3 block max-w-sm text-sm font-semibold text-slate-700">
                  Momento da avaliação
                  <select
                    aria-label="Momento do documento de avaliação"
                    value={documentModel}
                    onChange={(event) => changeDocumentModel(event.target.value as 'initial' | 'final')}
                    className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="initial">Inicial</option>
                    <option value="final">Final / alta</option>
                  </select>
                </label>
              )}
              <button
                type="button"
                onClick={() => changeDocumentModel('summary')}
                className={`mt-3 text-xs font-semibold underline ${documentModel === 'summary' ? 'text-primary-700' : 'text-slate-500'}`}
              >
                {documentModel === 'summary' ? 'Resumo simples selecionado' : 'Usar organização de resumo simples'}
              </button>
            </fieldset>

            <div className="mt-5 text-sm font-bold text-slate-900">Conteúdo e responsabilidade profissional</div>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {ASSESSMENT_REPORT_MODE_CATALOG.map((mode) => {
                const disabled =
                  editing
                  || (
                    mode.value === 'physiotherapy_technical_report'
                    && !canCreateTechnicalReport
                  );
                return (
                  <label
                    key={mode.value}
                    className={`rounded-2xl border p-4 ${
                      form.mode === mode.value
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-slate-200 bg-white'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="report-mode"
                        checked={form.mode === mode.value}
                        disabled={disabled}
                        onChange={() => {
                          if (
                            mode.value === 'assessment_summary'
                            && hasTechnicalOnlyContent(form)
                            && !window.confirm(
                              'Alterar para o resumo removerá os campos técnicos, o diagnóstico CBDF e o perfil CIF deste rascunho. Deseja continuar?',
                            )
                          ) {
                            return;
                          }
                          emit(withMode(form, mode.value), { fillEmpty: true });
                        }}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-bold text-slate-900">{mode.label}</div>
                        <div className="mt-1 text-sm text-slate-600">{mode.description}</div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {editing && (
              <p className="mt-2 text-xs text-slate-500">
                O tipo do documento não muda após o primeiro salvamento. Para emitir no outro
                formato, crie um novo relatório.
              </p>
            )}
            {!canCreateTechnicalReport && (
              <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                O relatório técnico com diagnóstico fisioterapêutico exige perfil de fisioterapeuta e CREFITO cadastrado.
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3">
              <div className="text-sm font-bold text-primary-900">
                {form.mode === 'physiotherapy_technical_report' ? 'Diagnóstico CBDF incluído neste documento' : 'Diagnóstico fisioterapêutico com CBDF'}
              </div>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                Registre o diagnóstico para o momento avaliado e vincule os achados que o sustentam. Na análise longitudinal, selecione as evidências com suas datas; uma mudança de medida não altera automaticamente a classificação.
              </p>
              {form.mode === 'physiotherapy_technical_report' ? (
                <button
                  type="button"
                  onClick={() => document.getElementById('assessment-report-diagnosis-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="mt-2 text-xs font-bold text-primary-800 underline"
                >
                  Revisar diagnóstico CBDF e perfil CIF
                </button>
              ) : canCreateTechnicalReport && !editing ? (
                <button
                  type="button"
                  onClick={() => emit(withMode(form, 'physiotherapy_technical_report'), { fillEmpty: true })}
                  className="mt-2 text-xs font-bold text-primary-800 underline"
                >
                  Incluir diagnóstico CBDF neste modelo
                </button>
              ) : editing ? (
                <p className="mt-2 text-xs text-slate-600">Para incluir diagnóstico em um resumo já salvo, crie um novo documento no modo técnico.</p>
              ) : null}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Destinatário</label>
                <select
                  value={form.audience.type}
                  onChange={(event) =>
                    emit({
                      ...form,
                      audience: {
                        ...form.audience,
                        type: event.target.value as typeof form.audience.type,
                      },
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                >
                  {ASSESSMENT_REPORT_AUDIENCE_CATALOG.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Nome do destinatário</label>
                <input
                  value={form.audience.recipientName ?? ''}
                  onChange={(event) =>
                    emit({
                      ...form,
                      audience: { ...form.audience, recipientName: event.target.value || null },
                    })
                  }
                  placeholder="Opcional"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Função / instituição</label>
                <input
                  value={[form.audience.recipientRole, form.audience.organization].filter(Boolean).join(' — ')}
                  onChange={(event) =>
                    emit({
                      ...form,
                      audience: { ...form.audience, recipientRole: event.target.value || null, organization: null },
                    })
                  }
                  placeholder="Ex.: pneumologista — hospital"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              {form.audience.type === 'other' && (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Descrição do destinatário</label>
                  <input
                    value={form.audience.customLabel ?? ''}
                    onChange={(event) =>
                      emit({
                        ...form,
                        audience: { ...form.audience, customLabel: event.target.value || null },
                      })
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Finalidade</label>
                <select
                  value={form.purpose.type}
                  onChange={(event) =>
                    emit({
                      ...form,
                      purpose: {
                        ...form.purpose,
                        type: event.target.value as typeof form.purpose.type,
                      },
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                >
                  {ASSESSMENT_REPORT_PURPOSE_CATALOG.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{form.purpose.type === 'other' ? 'Descreva a finalidade' : 'Contexto adicional (opcional)'}</label>
                <input
                  value={form.purpose.description}
                  onChange={(event) =>
                    emit({
                      ...form,
                      purpose: { ...form.purpose, description: event.target.value },
                    })
                  }
                  placeholder={form.purpose.type === 'other' ? 'Informe a finalidade do documento' : 'Inclua apenas uma informação específica deste documento'}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              {form.purpose.type === 'referral' && (
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Pergunta do encaminhamento</label>
                  <input
                    value={form.purpose.referralQuestion ?? ''}
                    onChange={(event) =>
                      emit({
                        ...form,
                        purpose: { ...form.purpose, referralQuestion: event.target.value || null },
                      })
                    }
                    placeholder="Qual questão o destinatário precisa responder?"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-950">2. Avaliações realizadas</h3>
                <p className="text-sm text-slate-500">
                  Selecione as avaliações que deseja apresentar no documento.
                </p>
              </div>
            </div>

            <div className="grid gap-3 rounded-2xl bg-slate-50 p-3 md:grid-cols-2 lg:grid-cols-5">
              <label className="relative lg:col-span-2">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  aria-label="Buscar avaliações concluídas"
                  value={sourceSearch}
                  onChange={(event) => setSourceSearch(event.target.value)}
                  placeholder="Buscar teste, resultado ou profissional"
                  className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm"
                />
              </label>
              <select
                aria-label="Filtrar avaliações por instrumento"
                value={sourceTestFilter}
                onChange={(event) => setSourceTestFilter(event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="all">Todos os testes</option>
                {testOptions.map((testKey) => (
                  <option key={testKey} value={testKey}>{getAssessmentV2Label(testKey)}</option>
                ))}
              </select>
              <select
                aria-label="Filtrar avaliações por profissional"
                value={sourceProfessionalFilter}
                onChange={(event) => setSourceProfessionalFilter(event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="all">Todos os profissionais</option>
                {professionalOptions.map((name) => <option key={name}>{name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  aria-label="Data inicial"
                  value={sourceDateFrom}
                  onChange={(event) => setSourceDateFrom(event.target.value)}
                  className="min-w-0 rounded-xl border border-slate-300 bg-white px-2 py-2 text-xs"
                />
                <input
                  type="date"
                  aria-label="Data final"
                  value={sourceDateTo}
                  onChange={(event) => setSourceDateTo(event.target.value)}
                  className="min-w-0 rounded-xl border border-slate-300 bg-white px-2 py-2 text-xs"
                />
              </div>
            </div>

            <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
              {!form.patient.patientId ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                  Selecione o paciente para carregar as avaliações.
                </div>
              ) : candidatesLoading ? (
                <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">Carregando histórico completo...</div>
              ) : filteredCandidates.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                  Nenhuma avaliação concluída corresponde aos filtros.
                </div>
              ) : (
                filteredCandidates.map((candidate) => {
                  const selected = selectedSourceIds.has(candidate.resultId);
                  return (
                    <label
                      key={candidate.resultId}
                      className={`grid cursor-pointer gap-2 rounded-2xl border px-4 py-3 md:grid-cols-[auto_1.4fr_0.8fr_1fr_1fr] md:items-center ${
                        selected ? 'border-primary-300 bg-primary-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSource(candidate)}
                      />
                      <div>
                        <div className="font-bold text-slate-900">{getAssessmentV2Label(candidate.testKey)}</div>
                        <div className="text-xs text-slate-500">{candidate.testKey}</div>
                      </div>
                      <div className="text-sm text-slate-700">{formatDate(candidate.assessmentDate)}</div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{getResultMetric(candidate)}</div>
                        <div className="text-xs text-slate-500">resultado registrado</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-700">{getResultClassification(candidate)}</div>
                        <div className="text-xs text-slate-500">
                          {candidate.professionalNameSnapshot || 'Profissional não identificado'}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {form.sources.length > 0 && (
              <div className="mt-5 space-y-3">
                <div>
                  <h4 className="font-bold text-slate-900">Resultados selecionados</h4>
                  <p className="text-sm text-slate-500">
                    Revise os resultados e, se necessário, ajuste a classificação, a referência ou a interpretação.
                  </p>
                </div>
                {form.sources.map((source) => {
                  const currentCandidate = candidateById.get(source.resultId);
                  const snapshotTimestamp =
                    source.snapshot.sourceUpdatedAt ?? source.snapshot.sourceCreatedAt;
                  const currentTimestamp =
                    currentCandidate?.updatedAt ?? currentCandidate?.createdAt ?? null;
                  const sourceChanged = Boolean(
                    snapshotTimestamp
                    && currentTimestamp
                    && snapshotTimestamp !== currentTimestamp,
                  );
                  const sourceUnavailable = !candidatesLoading && !currentCandidate;
                  const findingIsInterpretable =
                    source.finding.status === 'within_expected'
                    || source.finding.status === 'borderline'
                    || source.finding.status === 'altered';

                  return (
                  <article key={source.resultId} className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-bold text-slate-950">
                          {source.snapshot.testLabel} • {formatDate(source.snapshot.assessmentDate)}
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          <strong>{source.snapshot.primaryMeasure.formattedValue}</strong>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{sourceProfessionalLabel(source)}</div>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {findingIsInterpretable ? (
                          <span className={`rounded-full border px-3 py-1 text-xs font-bold ${findingTone[source.finding.status]}`}>
                            {getAssessmentReportFindingStatusLabel(source.finding.status)}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => onRemoveSource(source.resultId)}
                          className="inline-flex items-center rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                          aria-label={`Remover ${source.snapshot.testLabel} do relatório`}
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          Remover
                        </button>
                      </div>
                    </div>
                    {(sourceChanged || sourceUnavailable) && (
                      <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        {sourceChanged
                          ? 'A avaliação original foi alterada. Remova esta linha e selecione-a novamente para revisar o snapshot atualizado.'
                          : 'A avaliação original não está mais disponível entre as avaliações concluídas. Remova esta linha para prosseguir.'}
                      </div>
                    )}
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                        Classificação original (informativa)
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        {source.snapshot.referenceText || 'Não registrada pela avaliação de origem.'}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        Este texto não é usado automaticamente como critério ou ponto de corte do relatório.
                      </p>
                    </div>
                    {source.clinicalReference && (
                      <details className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-950">
                        <summary className="cursor-pointer font-bold">
                          Auditoria das referências clínicas — motor {source.clinicalReference.engineVersion}
                        </summary>
                        <div className="mt-2 space-y-2">
                          <p>
                            Versão do instrumento: <strong>{source.clinicalReference.instrumentVersion}</strong>
                            {source.clinicalReference.descriptiveOnly ? ' • sem classificação automática' : ''}
                          </p>
                          {source.clinicalReference.selectedRules.length > 0 && (
                            <div>
                              <div className="font-semibold">Regras selecionadas</div>
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {source.clinicalReference.selectedRules.map((rule) => (
                                  <li key={`${rule.id}-${rule.version}`}>
                                    {rule.reportLabel} (regra {rule.id}, v{rule.version})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {source.clinicalReference.excludedRules.length > 0 && (
                            <div>
                              <div className="font-semibold">Regras não aplicadas</div>
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {source.clinicalReference.excludedRules.map((rule) => (
                                  <li key={`${rule.ruleId}-${rule.ruleVersion}`}>
                                    {rule.explanation} ({rule.reason})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {source.clinicalReference.citations.length > 0 && (
                            <div>
                              <div className="font-semibold">Documentos vinculados</div>
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {source.clinicalReference.citations.map((citation) => (
                                  <li key={citation.id}>{citation.citation}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Domínio</label>
                        <select
                          aria-label={`Domínio clínico de ${source.snapshot.testLabel}`}
                          value={source.finding.domain}
                          onChange={(event) =>
                            updateSource(source.resultId, (current) => ({
                              ...current,
                              finding: {
                                ...current.finding,
                                domain: event.target.value as AssessmentReportDomain,
                              },
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        >
                          {ASSESSMENT_REPORT_DOMAIN_CATALOG.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Status do achado</label>
                        <select
                          aria-label={`Status do achado de ${source.snapshot.testLabel}`}
                          value={source.finding.status}
                          onChange={(event) => {
                            const status = event.target.value as AssessmentReportFindingStatus;
                            updateSource(source.resultId, (current) => ({
                              ...current,
                              finding: {
                                ...current.finding,
                                status,
                                basis:
                                  status === 'not_classified' || status === 'not_interpretable'
                                    ? 'descriptive_only'
                                    : 'professional_review',
                              },
                            }));
                          }}
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        >
                          {ASSESSMENT_REPORT_FINDING_STATUS_CATALOG.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.value === 'not_classified' ? 'Apresentar apenas a medida' : item.value === 'not_interpretable' ? 'Sem interpretação registrada' : item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                          Critério / referência adotado{findingIsInterpretable ? ' (obrigatório)' : ''}
                        </label>
                        <input
                          aria-label={`Critério ou referência adotado para ${source.snapshot.testLabel}`}
                          value={source.finding.referenceLabel ?? ''}
                          onChange={(event) =>
                            updateSource(source.resultId, (current) => ({
                              ...current,
                              finding: {
                                ...current.finding,
                                referenceLabel: event.target.value || null,
                              },
                            }))
                          }
                          placeholder="Ex.: ponto de corte, faixa normativa, manual ou diretriz adotada"
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                          Interpretação do resultado (opcional)
                        </label>
                        <input
                          aria-label={`Interpretação concisa de ${source.snapshot.testLabel}`}
                          value={source.finding.conciseSummary ?? ''}
                          onChange={(event) =>
                            updateSource(source.resultId, (current) => ({
                              ...current,
                              finding: {
                                ...current.finding,
                                conciseSummary: event.target.value || null,
                              },
                            }))
                          }
                          placeholder="Acrescente uma interpretação específica, se necessário"
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </section>

          <AssessmentClinicalSourcesPanel sources={form.sources} collapsible />

          {documentModel === 'longitudinal' && (
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-950">Evolução longitudinal dos resultados selecionados</h3>
              <p className="mb-4 mt-1 text-sm leading-relaxed text-slate-500">
                Selecione as avaliações de todas as datas que deseja apresentar. Posição, lado e protocolo permanecem em séries próprias. Medidas feitas uma única vez também podem integrar o documento.
              </p>
              <AssessmentLongitudinalPanel sources={form.sources}
                showCharts={form.exportOptions.longitudinalCharts !== false}
                onShowChartsChange={(longitudinalCharts) => emit({
                  ...form,
                  exportOptions: { ...form.exportOptions, longitudinalCharts },
                })} />
            </section>
          )}

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="font-bold text-slate-950">3. {documentModel === 'longitudinal' ? 'Análise profissional da evolução' : 'Análise profissional'}</h3>
              <p className="mt-1 text-sm text-slate-500">
                Registre o significado dos achados e as informações que deseja acrescentar aos resultados.
              </p>
            </div>
            {form.mode === 'assessment_summary' ? (
              <div className="grid gap-4">
                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Síntese profissional (opcional)
                  </span>
                  <textarea
                    rows={documentModel === 'longitudinal' ? 7 : 4}
                    value={form.content.professionalOverview}
                    onChange={(event) =>
                      emit({
                        ...form,
                        content: { ...form.content, professionalOverview: event.target.value },
                      })
                    }
                    placeholder={documentModel === 'longitudinal'
                      ? 'Integre as mudanças das medidas, os acontecimentos clínicos, as repercussões funcionais e as metas. Registre melhora, manutenção ou piora conforme sua análise.'
                      : 'Em poucas linhas: o que foi avaliado, quais são os achados que importam e como devem ser lidos em conjunto.'}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm leading-relaxed"
                  />
                </label>
                <div className="grid gap-4 md:grid-cols-2">
                  <label>
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Plano e reavaliação (opcional)
                    </span>
                    <textarea
                      rows={3}
                      value={form.content.followUpGuidance ?? ''}
                      onChange={(event) =>
                        emit({
                          ...form,
                          content: { ...form.content, followUpGuidance: event.target.value || null },
                        })
                      }
                      placeholder="Opcional"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label>
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">Observações do profissional (opcional)</span>
                    <textarea
                      rows={3}
                      value={joinLines(form.content.observations)}
                      onChange={(event) =>
                        emit({
                          ...form,
                          content: { ...form.content, observations: event.target.value ? [event.target.value] : [] },
                        })
                      }
                      placeholder="Acrescente informações relevantes para este documento"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Contexto clínico-funcional
                  </span>
                  <textarea
                    rows={4}
                    value={form.content.clinicalContext}
                    onChange={(event) =>
                      emit({ ...form, content: { ...form.content, clinicalContext: event.target.value } })
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Síntese profissional
                  </span>
                  <textarea
                    rows={4}
                    value={form.content.integratedAnalysis}
                    onChange={(event) =>
                      emit({ ...form, content: { ...form.content, integratedAnalysis: event.target.value } })
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Conclusão fisioterapêutica
                  </span>
                  <textarea
                    rows={4}
                    value={form.content.physiotherapyConclusion}
                    onChange={(event) =>
                      emit({
                        ...form,
                        content: { ...form.content, physiotherapyConclusion: event.target.value },
                      })
                    }
                    placeholder="Registre sua conclusão a partir dos achados e da avaliação clínica"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">Plano e reavaliação (opcional)</span>
                  <textarea
                    rows={4}
                    value={joinLines(form.content.recommendations)}
                    onChange={(event) =>
                      emit({
                        ...form,
                        content: { ...form.content, recommendations: splitLines(event.target.value) },
                      })
                    }
                    placeholder="Registre as condutas, metas ou o momento de reavaliação que definiu"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="md:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">Observações do profissional (opcional)</span>
                  <textarea
                    rows={2}
                    value={form.content.limitations ?? ''}
                    onChange={(event) =>
                      emit({
                        ...form,
                        content: { ...form.content, limitations: event.target.value || null },
                      })
                    }
                    placeholder="Acrescente informações relevantes para este documento"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            )}
          </section>

          {form.mode === 'physiotherapy_technical_report' && (
            <section id="assessment-report-diagnosis-editor" className="scroll-mt-5 rounded-3xl border border-primary-200 bg-white p-5 shadow-sm">
              <div className="mb-5">
                <div className="flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary-600" />
                  <h3 className="font-bold text-slate-950">4. Diagnóstico fisioterapêutico e perfil funcional</h3>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  Por regra clínica deste modo no RevaHub, o fisioterapeuta deve registrar
                  manualmente ao menos um diagnóstico CBDF-1 e um item CIF. O sistema não
                  converte escores em diagnóstico nem sugere códigos ou qualificadores. Os
                  pontos de atenção acima servem apenas para localizar as evidências que devem
                  ser revisadas antes dessa escolha.
                </p>
              </div>

              <div className="mb-5 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3">
                <div className="text-xs font-black uppercase tracking-wide text-primary-700">
                  Evidências para revisar
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">
                  {priorityEvidenceSources.length > 0
                    ? `Priorize a correlação clínica de ${priorityEvidenceSources
                        .map(sourceEvidenceLabel)
                        .join(', ')}. O sistema não indica códigos: consulte as classificações oficiais e vincule somente as avaliações que realmente sustentam sua decisão.`
                    : 'Nenhum achado está marcado como alterado ou em faixa de atenção. Revise as avaliações selecionadas e vincule somente as evidências que realmente sustentam sua decisão clínica.'}
                </p>
              </div>

              <div className="space-y-5 rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-slate-900">CBDF-1 — diagnóstico fisioterapêutico</h4>
                    <p className="text-xs text-slate-500">Selecione o capítulo-base e registre a formulação adotada pelo fisioterapeuta.</p>
                  </div>
                  <a
                    href={CBDF_OFFICIAL_SOURCE_URI}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-primary-700 underline"
                  >
                    Consultar página oficial (Anexos I e II)
                  </a>
                </div>

                {form.cbdf.entries.map((entry) => (
                  <div key={entry.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3">
                    <div>
                      <div className="text-sm font-bold text-slate-900">
                        {entry.manualCode || entry.chapterCode} — {entry.professionalStatement}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">Justificativa: {entry.rationale}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        Evidências: {entry.evidenceResultIds.map((id) => {
                          const source = form.sources.find((item) => item.resultId === id);
                          return source ? sourceEvidenceLabel(source) : id;
                        }).join('; ')}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Confirmado por {entry.recordedBy.name || 'fisioterapeuta'} · {formatDate(entry.recordedAt)}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Remover entrada CBDF"
                      onClick={() =>
                        emit({
                          ...form,
                          cbdf: {
                            ...form.cbdf,
                            entries: form.cbdf.entries.filter((item) => item.id !== entry.id),
                          },
                        })
                      }
                      className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <div className="grid gap-3 md:grid-cols-2">
                  <select
                    aria-label="Capítulo CBDF"
                    value={cbdfChapter}
                    onChange={(event) => setCbdfChapter(event.target.value as CbdfChapterCode | '')}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Selecione o capítulo-base...</option>
                    {CBDF_CHAPTER_CATALOG.map((chapter) => (
                      <option key={chapter.code} value={chapter.code}>{chapter.code} — {chapter.label}</option>
                    ))}
                  </select>
                  <input
                    aria-label="Código CBDF completo"
                    value={cbdfCode}
                    onChange={(event) => setCbdfCode(event.target.value)}
                    placeholder="Ex.: D04.01.4.4.4.2"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    aria-label="Descrição profissional do diagnóstico CBDF"
                    value={cbdfStatement}
                    onChange={(event) => setCbdfStatement(event.target.value)}
                    placeholder="Descrição profissional do diagnóstico"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    aria-label="Justificativa clínica do diagnóstico CBDF"
                    value={cbdfRationale}
                    onChange={(event) => setCbdfRationale(event.target.value)}
                    placeholder="Justificativa clínica"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Estrutura esperada: {cbdfChapter || 'capítulo'}.NN.N.N.N.N. O RevaHub valida
                  somente a estrutura e o capítulo; confira a existência e a transcrição do
                  código nos Anexos I e II e no sistema oficial da CBDF.
                </p>
                {cbdfChapter && cbdfCode.trim() && !isCbdfFullCodeForChapter(cbdfCode, cbdfChapter) && (
                  <p className="text-xs font-medium text-rose-700">
                    Use {cbdfChapter}.NN.N.N.N.N, com um subcódigo de dois dígitos e quatro
                    subcódigos de um dígito.
                  </p>
                )}
                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Avaliações que sustentam esta entrada</div>
                  <div className="flex flex-wrap gap-2">
                    {form.sources.map((source) => (
                      <label key={source.resultId} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={cbdfEvidenceResultIds.includes(source.resultId)}
                          onChange={() =>
                            toggleDraftEvidence(
                              source.resultId,
                              cbdfEvidenceResultIds,
                              setCbdfEvidenceResultIds,
                            )
                          }
                          className="mr-2"
                        />
                        {sourceEvidenceLabel(source)}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addCbdfEntry}
                  disabled={
                    !cbdfChapter
                    || !isCbdfFullCodeForChapter(cbdfCode, cbdfChapter)
                    || !cbdfStatement.trim()
                    || !cbdfRationale.trim()
                    || cbdfEvidenceResultIds.length === 0
                  }
                  className="inline-flex items-center rounded-xl bg-primary-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Incluir diagnóstico CBDF
                </button>
              </div>

              <div className="mt-5 space-y-5 rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-slate-900">CIF — perfil de funcionalidade</h4>
                    <p className="text-xs text-slate-500">
                      Edição {ICF_CLASSIFICATION_VERSION}, idioma {ICF_CLASSIFICATION_LANGUAGE}
                      {' '}(português). O RevaHub valida a estrutura e o componente; confirme a
                      existência do código na fonte oficial.
                    </p>
                  </div>
                  <a
                    href={ICF_CLASSIFICATION_SOURCE_URI}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-primary-700 underline"
                  >
                    Consultar edição oficial
                  </a>
                </div>

                {getIcfEntries(form).map((entry) => (
                  <div key={entry.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3">
                    <div>
                      <div className="text-sm font-bold text-slate-900">
                        {formatIcfQualifiedCode(
                          entry.code,
                          entry.qualifier,
                          entry.capacityQualifier,
                        )} — {entry.professionalDescription}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        {entry.domain === 'activities_and_participation'
                          ? [
                              `Desempenho: ${getIcfQualifierLabel(entry.qualifier, entry.domain)}`,
                              entry.capacityQualifier !== null
                                ? `Capacidade: ${getIcfQualifierLabel(entry.capacityQualifier, entry.domain)}`
                                : null,
                            ].filter(Boolean).join(' • ')
                          : `${getIcfQualifierContextLabel(entry.qualifierContext)} • ${getIcfQualifierLabel(entry.qualifier, entry.domain)}`}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Evidências: {entry.evidenceResultIds.map((id) => {
                          const source = form.sources.find((item) => item.resultId === id);
                          return source ? sourceEvidenceLabel(source) : id;
                        }).join('; ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Remover entrada CIF"
                      onClick={() =>
                        emit(replaceIcfEntries(form, getIcfEntries(form).filter((item) => item.id !== entry.id)))
                      }
                      className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <div className="grid gap-3 md:grid-cols-2">
                  <select
                    aria-label="Componente CIF"
                    value={icfDraft.domain}
                    onChange={(event) =>
                      setIcfDraft({
                        ...icfDraft,
                        domain: event.target.value as IcfDomain,
                        code: '',
                        qualifierContext:
                          event.target.value === 'activities_and_participation'
                            ? 'performance'
                            : '',
                        qualifier: '',
                        capacityQualifier: '',
                      })
                    }
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    {ICF_DOMAIN_CATALOG.map((domain) => (
                      <option key={domain.value} value={domain.value}>{domain.label}</option>
                    ))}
                  </select>
                  <input
                    aria-label="Código CIF"
                    value={icfDraft.code}
                    onChange={(event) => setIcfDraft({ ...icfDraft, code: event.target.value })}
                    placeholder={`Ex.: ${ICF_DOMAIN_CATALOG.find((item) => item.value === icfDraft.domain)?.prefix}1 ou ${ICF_DOMAIN_CATALOG.find((item) => item.value === icfDraft.domain)?.prefix}110`}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                  <select
                    aria-label="Tipo do qualificador CIF"
                    value={icfDraft.qualifierContext}
                    onChange={(event) =>
                      setIcfDraft({
                        ...icfDraft,
                        qualifierContext: event.target.value as IcfQualifierContext | '',
                        qualifier: '',
                        capacityQualifier: '',
                      })
                    }
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Escolha o tipo do qualificador...</option>
                    {(icfDraft.domain === 'activities_and_participation'
                      ? ['performance']
                      : icfDraft.domain === 'environmental_factors'
                        ? ['barrier', 'facilitator']
                        : ['extent']
                    ).map((context) => (
                      <option key={context} value={context}>
                        {getIcfQualifierContextLabel(context as IcfQualifierContext)}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label={
                      icfDraft.domain === 'activities_and_participation'
                        ? 'Qualificador CIF de desempenho'
                        : 'Qualificador CIF'
                    }
                    value={icfDraft.qualifier}
                    onChange={(event) => setIcfDraft({ ...icfDraft, qualifier: event.target.value })}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Escolha o qualificador...</option>
                    {icfDraft.qualifierContext !== 'facilitator' && ICF_PROBLEM_QUALIFIER_CATALOG.map((qualifier) => (
                      <option key={qualifier.value} value={String(qualifier.value)}>
                        {qualifier.value} — {icfDraft.domain === 'environmental_factors'
                          ? getIcfQualifierLabel(qualifier.value, icfDraft.domain)
                          : qualifier.label}
                      </option>
                    ))}
                    {icfDraft.qualifierContext === 'facilitator' && ICF_ENVIRONMENTAL_FACILITATOR_CATALOG.map((qualifier) => (
                      <option key={qualifier.value} value={qualifier.value}>{qualifier.value} — {qualifier.label}</option>
                    ))}
                  </select>
                  {icfDraft.domain === 'activities_and_participation' && (
                    <select
                      aria-label="Qualificador CIF de capacidade"
                      value={icfDraft.capacityQualifier}
                      onChange={(event) =>
                        setIcfDraft({
                          ...icfDraft,
                          capacityQualifier: event.target.value,
                        })
                      }
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">Capacidade não registrada (opcional)</option>
                      {ICF_PROBLEM_QUALIFIER_CATALOG.map((qualifier) => (
                        <option key={qualifier.value} value={String(qualifier.value)}>
                          {qualifier.value} — {qualifier.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    aria-label="Descrição funcional da entrada CIF"
                    value={icfDraft.professionalDescription}
                    onChange={(event) =>
                      setIcfDraft({ ...icfDraft, professionalDescription: event.target.value })
                    }
                    placeholder="Descrição funcional observada"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    aria-label="Contexto ou observação da entrada CIF"
                    value={icfDraft.note}
                    onChange={(event) => setIcfDraft({ ...icfDraft, note: event.target.value })}
                    placeholder="Contexto/observação (opcional)"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Use um capítulo de um dígito ou um código de 3 a 5 dígitos após o componente;
                  códigos de dois dígitos não são válidos. Capítulos permitidos: b/s 1–8, d 1–9
                  e e 1–5. Em atividades e participação, desempenho ocupa o primeiro
                  qualificador e capacidade, quando registrada, o segundo (ex.: d450.12).
                </p>
                {icfDraft.code.trim() && !isIcfCodeForDomain(icfDraft.code, icfDraft.domain) && (
                  <p className="text-xs font-medium text-rose-700">
                    A estrutura não corresponde ao componente selecionado. A validação não
                    confirma a existência do código na CIF.
                  </p>
                )}
                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Avaliações que sustentam esta entrada</div>
                  <div className="flex flex-wrap gap-2">
                    {form.sources.map((source) => (
                      <label key={source.resultId} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={icfDraft.evidenceResultIds.includes(source.resultId)}
                          onChange={() =>
                            setIcfDraft({
                              ...icfDraft,
                              evidenceResultIds: icfDraft.evidenceResultIds.includes(source.resultId)
                                ? icfDraft.evidenceResultIds.filter((id) => id !== source.resultId)
                                : [...icfDraft.evidenceResultIds, source.resultId],
                            })
                          }
                          className="mr-2"
                        />
                        {sourceEvidenceLabel(source)}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addIcfEntry}
                  disabled={
                    !isIcfCodeForDomain(icfDraft.code, icfDraft.domain)
                    || !icfDraft.qualifierContext
                    || !icfDraft.qualifier
                    || !icfDraft.professionalDescription.trim()
                    || icfDraft.evidenceResultIds.length === 0
                  }
                  className="inline-flex items-center rounded-xl bg-primary-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Incluir item CIF
                </button>
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="font-bold text-slate-950">{form.mode === 'physiotherapy_technical_report' ? '5' : '4'}. Entrega e histórico</h3>
              <p className="text-sm text-slate-500">O padrão é somente o relatório. Anexos e comparações dependem de seleção explícita.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Conteúdo do PDF</label>
                <select
                  value={form.exportOptions.attachments.mode}
                  onChange={(event) => {
                    const mode = event.target.value as typeof form.exportOptions.attachments.mode;
                    emit({
                      ...form,
                      exportOptions: {
                        ...form.exportOptions,
                        attachments: {
                          mode,
                          resultIds:
                            mode === 'selected_assessments'
                              ? form.exportOptions.attachments.resultIds
                              : [],
                        },
                      },
                    });
                  }}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                >
                  {ASSESSMENT_REPORT_ATTACHMENT_MODE_CATALOG.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                {form.exportOptions.attachments.mode === 'selected_assessments' && (
                  <div className="mt-3 space-y-2">
                    {form.sources.map((source) => (
                      <label key={source.resultId} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={form.exportOptions.attachments.resultIds.includes(source.resultId)}
                          onChange={() => toggleAttachment(source.resultId)}
                        />
                        {source.snapshot.testLabel} • {formatDate(source.snapshot.assessmentDate)}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Comparação histórica</label>
                <select
                  value={form.exportOptions.history.mode}
                  onChange={(event) => {
                    const mode = event.target.value as typeof form.exportOptions.history.mode;
                    emit({
                      ...form,
                      exportOptions: {
                        ...form.exportOptions,
                        history: { mode, selections: mode === 'none' ? [] : form.exportOptions.history.selections },
                      },
                    });
                  }}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="none">Sem histórico</option>
                  <option value="selected">Comparações selecionadas</option>
                </select>
                {form.exportOptions.history.mode === 'selected' && (
                  <div className="mt-3 max-h-64 space-y-3 overflow-y-auto">
                    {form.sources.map((source) => {
                      const selection = form.exportOptions.history.selections.find(
                        (item) => item.sourceResultId === source.resultId,
                      );
                      const historyCandidates = candidates.filter(
                        (candidate) =>
                          candidate.testKey === source.snapshot.testKey
                          && candidate.resultId !== source.resultId
                          && new Date(candidate.assessmentDate).getTime()
                            < new Date(source.snapshot.assessmentDate).getTime(),
                      );
                      const candidateIds = new Set(
                        historyCandidates.map((candidate) => candidate.resultId),
                      );
                      const unavailableSelectedIds = candidatesLoading
                        ? []
                        : (selection?.historicalResultIds ?? []).filter(
                            (resultId) => !candidateIds.has(resultId),
                          );
                      if (
                        historyCandidates.length === 0
                        && unavailableSelectedIds.length === 0
                      ) {
                        return null;
                      }

                      return (
                        <div key={source.resultId} className="rounded-xl bg-slate-50 p-3">
                          <div className="mb-2 text-sm font-bold text-slate-900">{source.snapshot.testLabel}</div>
                          {historyCandidates.map((candidate) => {
                            const selected =
                              selection?.historicalResultIds.includes(candidate.resultId) ?? false;
                            const savedSnapshot = selection?.historicalResultSnapshots.find(
                              (snapshot) => snapshot.resultId === candidate.resultId,
                            );
                            const identityMatches = Boolean(
                              savedSnapshot
                              && savedSnapshot.sessionId === candidate.sessionId
                              && savedSnapshot.testKey === candidate.testKey
                              && savedSnapshot.testVersion === candidate.testVersion
                              && savedSnapshot.assessmentDate === candidate.assessmentDate
                              && savedSnapshot.sourceStatus === candidate.status,
                            );
                            const needsReview = selected && (
                              !savedSnapshot
                              || !identityMatches
                              || !revisionsMatch(
                                savedSnapshot.sourceUpdatedAt,
                                savedSnapshot.sourceCreatedAt,
                                candidate.updatedAt,
                                candidate.createdAt,
                              )
                            );

                            return (
                              <div
                                key={candidate.resultId}
                                className={`mb-2 rounded-lg border px-2.5 py-2 ${
                                  needsReview
                                    ? 'border-amber-300 bg-amber-50'
                                    : 'border-transparent bg-white'
                                }`}
                              >
                                <label className="flex items-center gap-2 text-xs text-slate-700">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleHistoricalResult(source.resultId, candidate)}
                                  />
                                  <span>
                                    {formatDate(candidate.assessmentDate)} • {getResultMetric(candidate)}
                                  </span>
                                </label>
                                {needsReview && (
                                  <div className="mt-2 flex items-start justify-between gap-2 text-xs text-amber-800">
                                    <span>
                                      Esta seleção não possui snapshot verificável ou foi alterada.
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => selectHistoricalResult(source.resultId, candidate)}
                                      className="shrink-0 font-bold underline underline-offset-2"
                                    >
                                      Revisar e atualizar
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {unavailableSelectedIds.map((resultId) => {
                            const savedSnapshot = selection?.historicalResultSnapshots.find(
                              (snapshot) => snapshot.resultId === resultId,
                            );
                            return (
                              <div
                                key={resultId}
                                className="mb-2 flex items-start justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-800"
                              >
                                <div>
                                  <div className="font-bold">Histórico indisponível — revisão obrigatória</div>
                                  <div className="mt-0.5">
                                    {savedSnapshot
                                      ? `${formatDate(savedSnapshot.assessmentDate)} • ${savedSnapshot.primaryResult}`
                                      : `Resultado ${resultId}`}
                                  </div>
                                  <div className="mt-0.5">
                                    O registro foi removido, invalidado ou não corresponde mais a esta avaliação.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeHistoricalResult(source.resultId, resultId)}
                                  aria-label={`Remover histórico ${resultId}`}
                                  className="rounded-lg p-1.5 text-rose-700 hover:bg-rose-100"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              {validation.valid ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-950">Revisão final</h3>
                {validation.issues.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-sm">
                    {validation.issues.slice(0, 10).map((issue, index) => (
                      <li
                        key={`${issue.path}-${issue.code}-${index}`}
                        className={issue.severity === 'error' ? 'text-rose-700' : 'text-amber-700'}
                      >
                        {issue.severity === 'error' ? '•' : 'Atenção:'} {issue.message}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-emerald-700">Todos os campos necessários estão prontos.</p>
                )}
                <label className="mt-4 flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                    className="mt-1"
                  />
                  Revisei os resultados e o texto deste documento. Confirmo a autoria da análise
                  e, quando incluídos, dos registros CBDF e CIF.
                </label>
              </div>
            </div>
          </section>
        </div>

        <footer className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between md:px-7">
          <div className="text-xs text-slate-500">
            Autor: {form.authorship.professional.name || 'não identificado'}
            {form.authorship.professional.registration ? ` • ${form.authorship.professional.registration}` : ''}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={saving || !confirmed || !validation.valid || technicalUnavailable}
              className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileText className="mr-2 h-4 w-4" />
              {saving ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar relatório'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AssessmentReportEditorModal;
